import { BRONCO_SUPPLIER_UUID, PRODUCTION_DEMO_CHATS } from '@/mocks/uuids';
import {
  broncoDemoActiveAlerts,
  dummyChurnRisks,
  dummyLostItems,
  dummyOverduePayments,
} from '@/hooks/use-dummy-data';
import {
  GetAllActiveHubAlertsQuery,
  GetAllActiveHubAlertsQueryVariables,
  HubCardType,
} from '@/types';
import { gql } from '@apollo/client';
import { HubCardData, SalesAlertsHubCardData } from './types';
import { partnerSupplier } from '@/utils/environment';
import { useEffect, useMemo, useState } from 'react';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

// Query all ACTIVE hub card alerts, hidden alerts are queried elsewhere
export const GET_ALL_ACTIVE_HUB_ALERTS_QUERY = gql`
  query getAllActiveHubAlerts(
    $businessOrganizationUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getAllHubAlerts(
      business_organization_id: $businessOrganizationUUID
      filters: { alert_statuses: ACTIVE }
      supplier_id: $supplierUUID
    ) {
      card_data_json
    }
  }
`;

export const useDemoInsightsHubData = (
  supplierUUID: string,
  assignedChatUUIDs: string[],
) => {
  const [loading, setLoading] = useState(true);
  const chatSet = new Set(assignedChatUUIDs);
  const unfiltered =
    supplierUUID === BRONCO_SUPPLIER_UUID
      ? broncoDemoActiveAlerts
      : [dummyOverduePayments, dummyLostItems, dummyChurnRisks];
  const data = unfiltered
    .map(
      (group) =>
        ({
          ...group,
          cardData: group.cardData.filter((card) =>
            chatSet.has(card.chat_uuid),
          ),
        }) as SalesAlertsHubCardData,
    )
    .filter((group) => group.cardData.length > 0);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return {
    data: loading ? undefined : data,
    error: false,
    loading,
  };
};

const useLiveInsightsHubAlerts = (
  businessOrganizationUUID: string,
  supplierUUID: string,
) => {
  const { data, loading, error } = useWrappedQuery<
    GetAllActiveHubAlertsQuery,
    GetAllActiveHubAlertsQueryVariables
  >(GET_ALL_ACTIVE_HUB_ALERTS_QUERY, {
    variables: {
      businessOrganizationUUID,
      supplierUUID,
    },
  });

  /**
   * The following transformer will parse the list of alerts and group them by card type.
   */
  const transformedCardData = useMemo(() => {
    // return an empty array if data is not available
    if (!data?.getAllHubAlerts.card_data_json) {
      return undefined;
    }

    // parse HubCardData and group by card type
    const parsedData = data.getAllHubAlerts.card_data_json.reduce(
      (alertMap, alert) => {
        const parsedAlert = JSON.parse(alert) as HubCardData;
        const alertType = parsedAlert.card_type;

        if (!alertMap[alertType]) {
          alertMap[alertType] = [];
        }

        alertMap[alertType].push(parsedAlert);
        return alertMap;
      },
      {} as Record<HubCardType, HubCardData[]>,
    );

    // transform HubCardData to SalesAlertsHubCardData
    return Object.entries(parsedData).map(([cardType, cardData]) => {
      return {
        cardData,
        cardType,
      } as SalesAlertsHubCardData;
    });
  }, [data]);

  return {
    data: loading ? undefined : transformedCardData,
    error,
    loading,
  };
};

export const useInsightsHubAlerts = () => {
  const { chatRestaurantMap, selectedRestaurantRelationship } =
    useUserContext();
  const selectedChatUUID = selectedRestaurantRelationship.chatUUIDs[0];
  const supplierUUID = selectedRestaurantRelationship.supplierUUID;

  const assignedChatUUIDs = useMemo(
    () => Object.keys(chatRestaurantMap),
    [chatRestaurantMap],
  );
  const demoQueryResults = useDemoInsightsHubData(
    supplierUUID,
    assignedChatUUIDs,
  );
  const liveQueryResults = useLiveInsightsHubAlerts(
    partnerSupplier.businessOrganizationUUID,
    supplierUUID,
  );

  if (PRODUCTION_DEMO_CHATS.some((chatUUID) => chatUUID === selectedChatUUID)) {
    return demoQueryResults;
  }

  return liveQueryResults;
};
